import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';

const sections = [
  { id: 'intro', label: 'Intro', path: '/' },
  { id: 'products', label: 'Products', path: '/products' },
  { id: 'solutions', label: 'Solutions', path: '/system-solutions' },
  { id: 'support', label: 'Support', path: '/service-support' },
  { id: 'contact', label: 'Contact', path: '/contact-ph' }
];

const VerticalSectionTracker = () => {
  const [activeSection, setActiveSection] = useState(0);
  const location = useLocation();
  
  useEffect(() => {
    // Set active section based on current route
    const currentIndex = sections.findIndex(s => s.path === location.pathname);
    if (currentIndex >= 0) setActiveSection(currentIndex);
    
    // Scroll handler for homepage sections
    const handleScroll = () => {
      if (location.pathname === '/') {
        const scrollPosition = window.scrollY + window.innerHeight / 2;
        const docHeight = document.documentElement.scrollHeight;
        const progress = scrollPosition / docHeight;
        
        if (progress < 0.2) setActiveSection(0);
        else if (progress < 0.4) setActiveSection(1);
        else if (progress < 0.6) setActiveSection(2);
        else if (progress < 0.8) setActiveSection(3);
        else setActiveSection(4);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);
  
  const scrollToSection = (index) => {
    const section = sections[index];
    if (section.path === location.pathname) {
      // Same page - scroll to position
      const targetScroll = (index / sections.length) * document.documentElement.scrollHeight;
      window.scrollTo({ top: targetScroll, behavior: 'smooth' });
    } else {
      // Different page - navigate
      window.location.href = section.path;
    }
  };
  
  return (
    <>
      {/* Desktop Version */}
      <div className="hidden lg:block fixed right-8 top-1/2 -translate-y-1/2 z-[100]">
        <div className="flex flex-col gap-6 items-end">
          {sections.map((section, index) => {
            const isActive = index === activeSection;
            
            return (
              <motion.button
                key={section.id}
                onClick={() => scrollToSection(index)}
                className="group flex items-center gap-3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <span 
                  className={`text-xs font-mono transition-all duration-300 ${
                    isActive 
                      ? 'text-[#00FFD1] opacity-100' 
                      : 'text-white/40 opacity-0 group-hover:opacity-100'
                  }`}
                >
                  {section.label}
                </span>
                
                <div className="relative">
                  <div 
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      isActive 
                        ? 'w-3 h-3 bg-[#00FFD1]' 
                        : 'bg-white/30 group-hover:bg-white/60'
                    }`}
                  />
                  
                  {isActive && (
                    <motion.div
                      layoutId="activeIndicator"
                      className="absolute inset-0 rounded-full"
                      style={{
                        boxShadow: '0 0 20px rgba(0, 255, 209, 0.6)'
                      }}
                    />
                  )}
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default VerticalSectionTracker;
